'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { deferOverlayComponent } from '@eliadmin/core/src/components/ui/dialog';
import type { MobileSidebarDialog as DialogRuntime } from './mobile-sidebar-dialog';

const MobileSidebarDialog = deferOverlayComponent<typeof DialogRuntime>(
  () => import('./mobile-sidebar-dialog').then(runtime => runtime.MobileSidebarDialog),
  'MobileSidebarDialog',
);

export function MobileSidebarBoundary({
  trigger, triggerLabel, triggerClassName, contentClassName, logo, children,
}: {
  trigger: ReactNode;
  triggerLabel: string;
  triggerClassName?: string;
  contentClassName?: string;
  logo: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [activated, setActivated] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  useEffect(() => {
    if (previousPathname.current !== pathname) {
      previousPathname.current = pathname;
      setOpen(false);
    }
  }, [pathname]);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onClick={() => {
          setActivated(true);
          setOpen(true);
        }}
      >
        {trigger}
      </button>
      {(open || activated) && (
        <MobileSidebarDialog
          open={open}
          onOpenChange={setOpen}
          triggerRef={triggerRef}
          dialogId={dialogId}
          className={contentClassName}
          logo={logo}
        >
          {children}
        </MobileSidebarDialog>
      )}
    </>
  );
}
